import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';

const MathDisplay = ({ children, latex, inline = false, className = '' }) => {
  // Use latex prop if provided, otherwise use children
  const content = latex || children;
  
  if (!content) return null;

  // Ensure content is a string
  const contentString = typeof content === 'string' ? content : String(content);

  // Function to parse mixed text and math content
  const parseContent = (text) => {
    if (!text) return [];
    
    // Ensure text is a string
    const textString = typeof text === 'string' ? text : String(text);
    
    // First split by newlines to preserve line breaks
    const lines = textString.split('\n');
    const allParts = [];
    
    lines.forEach((line, lineIndex) => {
      // Split each line by $ delimiters to separate math from text
      const parts = line.split(/(\$[^$]*\$)/g);
      
      const lineParts = parts.map((part, index) => {
        if (part.startsWith('$') && part.endsWith('$')) {
          // This is a math expression
          const mathContent = part.slice(1, -1); // Remove $ delimiters
          return {
            type: 'math',
            content: mathContent,
            key: `${lineIndex}-${index}`
          };
        } else if (part.trim() || part === '') {
          // This is regular text (including empty strings for spacing)
          return {
            type: 'text',
            content: part,
            key: `${lineIndex}-${index}`
          };
        }
        return null;
      }).filter(item => item !== null);
      
      allParts.push({
        type: 'line',
        content: lineParts,
        key: lineIndex,
        isLast: lineIndex === lines.length - 1
      });
    });
    
    return allParts;
  };

  // Clean up LaTeX content
  const cleanLatex = (latexString) => {
    if (!latexString) return '';
    
    // Ensure latexString is a string
    const latexStr = typeof latexString === 'string' ? latexString : String(latexString);
    
    return latexStr
      .replace(/\\\\/g, '\\')
      .replace(/\\text\{([^}]*)\}/g, '\\text{$1}')
      .replace(/\\,/g, '\\;')
      .trim();
  };

  const parsedContent = parseContent(contentString);

  // If it's a simple math expression without mixed content
  if (parsedContent.length === 1 && 
      parsedContent[0].type === 'line' && 
      parsedContent[0].content.length === 1 && 
      parsedContent[0].content[0].type === 'math') {
    const cleanedMath = cleanLatex(parsedContent[0].content[0].content);
    
    if (inline) {
      return (
        <span className={className}>
          <InlineMath math={cleanedMath} />
        </span>
      );
    }
    
    return (
      <div className={`text-center ${className}`}>
        <BlockMath math={cleanedMath} />
      </div>
    );
  }

  // Handle mixed content with line breaks
  if (inline) {
    return (
      <span className={className}>
        {parsedContent.map((line) => (
          <span key={line.key}>
            {line.content.map((part) => {
              if (part.type === 'math') {
                return <InlineMath key={part.key} math={cleanLatex(part.content)} />;
              }
              return <span key={part.key}>{part.content}</span>;
            })}
            {!line.isLast && ' '}
          </span>
        ))}
      </span>
    );
  }

  return (
    <div className={`${className} whitespace-pre-line`}>
      {parsedContent.map((line) => (
        <div key={line.key} className="mb-2 last:mb-0">
          {line.content.map((part) => {
            if (part.type === 'math') {
              return <InlineMath key={part.key} math={cleanLatex(part.content)} />;
            }
            return <span key={part.key}>{part.content}</span>;
          })}
        </div>
      ))}
    </div>
  );
};

export default MathDisplay;
